import React, {ReactElement, ReactNode} from "react";
import {
    BrowserRouter as Router,
    Link as ReactRouterLink,
    Route,
    RouteComponentProps,
    Switch
} from "react-router-dom";
import { CognitoUserInterface } from "@aws-amplify/ui-components";
import SurveyorLanding from "../components/SurveyorLanding";
import InspectionDetails from "../components/InspectionDetails";
import ErrorPage from "../components/error-page";
import { InspectionImages } from "../components/InspectionImages";
import MainFrameContextProvider from "../components/contexts/MainFrameContext";
import "react-toastify/dist/ReactToastify.css";
import { AppProvider } from "@shopify/polaris";
import ElementsContextProvider from "../components/contexts/ElementsContext";
import Sections from "../components/Sections/Sections";
import { AssetType } from "../types/AssetType";
import GoogleAnalytics from "../components/GoogleAnalytics";
import {useSelector} from "react-redux";
import {userIsDocCollector} from "../helpers/users";
import { getUserInfo } from "../services/UserService";
import Routes from "../config/api";
import InspectionDefects from "../components/InspectionDefects";
import { useLDClient } from "launchdarkly-react-client-sdk";
interface MatchParams {
    name: string;
    id: string;
}

type MatchProps = RouteComponentProps<MatchParams>;

interface LayoutProps {
    user: CognitoUserInterface;
}

interface PolarisProps extends React.HTMLAttributes<Element> {
    // add any custom props
    url: string;
    children?: ReactNode;
}

const theme = {
    colors: {
        primary: "#2D2859",
        highlight: "#5AD1D4",
        success: "#3BB54A",
        interactive: "#3A56AF"
    },
    logo: {
        width: 124,
        topBarSource: "/images/idwal-logo.svg",
        url: "/",
        accessibilityLabel: "Idwal Logo"
    }
};

// https://polaris.shopify.com/components/structure/app-provider#section-using-linkcomponent
function Link({ children, url = "", ...rest }: PolarisProps) {
    // Use an regular a tag for external and download links
    if (isOutboundLink(url) /*|| rest.download*/) {
        return (
            <a href={url} {...rest}>
                {children}
            </a>
        );
    }

    return (
        <ReactRouterLink to={url} {...rest}>
            {children}
        </ReactRouterLink>
    );
}

function DocumentCollectorLink({ children, url = "", ...rest }: PolarisProps) {
    if (isOutboundLink(url) /*|| rest.download*/) {
        return (
            <a href={url} {...rest}>
                {children}
            </a>
        );
    }

    if (url === "/") {
        return <div>{children}</div>
    }

    return (
        <ReactRouterLink to={url} {...rest}>
            {children}
        </ReactRouterLink>
    );
}

const SharedAccessProtectedRoute = ({ route, children } : any) => {
    const isSharedAccess = localStorage.getItem("InspectionViewOnly");
    const inspectionAccess = localStorage.getItem("Inspection");
    const inspection = route.params.name;
    if (isSharedAccess && inspection.toLowerCase() != inspectionAccess?.toLowerCase()) {
        return <ErrorPage errorCode={"401"} />
    }
    return children;
};

function isOutboundLink(url: string) {
    return /^(?:[a-z][a-z\d+.-]*:|\/\/)/.test(url);
}

export default function AppLayout({ user }: LayoutProps): ReactElement {
    const ldClient = useLDClient();
    const isDocumentCollector: boolean = useSelector((state: any) =>  userIsDocCollector(state));

    const modifyPolarisToIdwalBranding = () => {
        const app = document.querySelector(".App div") as HTMLElement;
        if (app) {
            let styles = app.getAttribute("style");
            if (styles) {
                const newStyles = `
                --p-text-primary: #2D2859 !important;
                --p-action-primary: #2D2859 !important;
                --p-icon: #5C5F62!important;
                --p-text: #5C5F62!important;
                --p-interactive: #2D2859!important;
                --p-icon-success: #3BB54A!important;
                --p-text-highlight #2D2859 !important;
                --p-border-highlight: #3BB54A !important;
`;
                styles += newStyles;
                app.setAttribute("style", styles);
            }
        }
    };

    modifyPolarisToIdwalBranding();

    React.useEffect(() => {

        async function checkTermsAccepted() {
            const userInfo = await getUserInfo(user.username);
            const acknowledgement = userInfo?.acknowledgement;
    
            if (!acknowledgement) {
                redirectToSSO();
            } else {
                ldClient?.identify({
                    key: user.username,
                    email: user.attributes?.email
                });
            }
        }
        checkTermsAccepted();
        
    }, [ldClient]);

    const redirectToSSO = () => {
        window.location.assign(
            `${Routes.authUrlProtocol}://${process.env.REACT_APP_AUTH_URL}?redirect=${window.location.href}`
        );
    };

    return (
        <Router>
            <AppProvider linkComponent={!isDocumentCollector ? Link : DocumentCollectorLink} theme={theme} i18n={{}}>
                <MainFrameContextProvider user={user}>
                    <div className="App">
                        <Switch>
                            <Route
                                path="/"
                                exact
                                component={() => <SurveyorLanding />}
                            />

                                <Route
                                    path="/inspection/:name"
                                    exact
                                    component={({ match }: MatchProps) => (
                                        <SharedAccessProtectedRoute route={match}>
                                            <InspectionDetails id={match.params.name} />
                                        </SharedAccessProtectedRoute>
                                    )}
                                />

                            <Route
                                path="/inspection/:name/documents"
                                exact
                                component={({ match }: MatchProps) => (
                                    <SharedAccessProtectedRoute route={match}>
                                        <ElementsContextProvider>
                                            <Sections
                                                assetType={AssetType.Document}
                                                id={match.params.name}
                                            />
                                        </ElementsContextProvider>
                                    </SharedAccessProtectedRoute>
                                )}
                            />

                            <Route
                                path="/inspection/:name/checklist"
                                exact
                                component={({ match }: MatchProps) => (
                                    <SharedAccessProtectedRoute route={match}>
                                        <ElementsContextProvider>
                                            <Sections
                                                assetType={AssetType.CheckList}
                                                id={match.params.name}
                                            />
                                        </ElementsContextProvider>
                                    </SharedAccessProtectedRoute>
                                )}
                            />

                            <Route
                                path="/inspection/:name/photos"
                                exact
                                component={({ match }: MatchProps) => (
                                    <SharedAccessProtectedRoute route={match}>
                                        <ElementsContextProvider>
                                            <InspectionImages
                                                id={match.params.name}
                                            />
                                        </ElementsContextProvider>
                                    </SharedAccessProtectedRoute>
                                )}
                            />

                            <Route
                                path="/inspection/:name/carbon-offsetting"
                                exact
                                component={({ match }: MatchProps) => (
                                    <SharedAccessProtectedRoute route={match}>
                                        <ElementsContextProvider>
                                            <Sections
                                                assetType={
                                                    AssetType.CarbonOffsetting
                                                }
                                                id={match.params.name}
                                            />
                                        </ElementsContextProvider>
                                    </SharedAccessProtectedRoute>
                                )}
                            />

                            <Route
                                path="/inspection/:name/photos/:id"
                                exact
                                component={({ match }: MatchProps) => (
                                    <SharedAccessProtectedRoute route={match}>
                                        <ElementsContextProvider>
                                            <InspectionImages
                                                id={match.params.name}
                                                elementIndex={match.params.id}
                                            />
                                        </ElementsContextProvider>
                                    </SharedAccessProtectedRoute>
                                )}
                            />

                            <Route
                                path="/inspection/:name/defects"
                                exact
                                component={({ match }: MatchProps) => (
                                    <SharedAccessProtectedRoute route={match}>
                                        <ElementsContextProvider>
                                            <InspectionDefects
                                                id={match.params.name}
                                            />
                                        </ElementsContextProvider>
                                    </SharedAccessProtectedRoute>
                                )}
                            />

                            <Route
                                path="/error/:name"
                                exact
                                component={({ match }: MatchProps) => (
                                    <ErrorPage errorCode={match.params.name} />
                                )}
                            />

                            <Route
                                path="*"
                                component={() => <ErrorPage errorCode={"404"} />}
                            />
                        </Switch>
                        <GoogleAnalytics />
                    </div>
                </MainFrameContextProvider>
            </AppProvider>
        </Router>
    );
}
